import { useState, useEffect } from "preact/hooks";
import { HeaderMinimal } from "../components/Header";
import { BrutalButton } from "../components/BrutalUI";
import { Container } from "../components/Container";

const RECENT_CALENDARS_KEY = "meetday_recent_calendars";

interface RecentCalendar {
  slug: string;
  name?: string;
  lastVisited: number;
}

function getRecentCalendars(): RecentCalendar[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(RECENT_CALENDARS_KEY);
    const list: RecentCalendar[] = raw ? JSON.parse(raw) : [];
    const cutoff = Date.now() - 1000 * 60 * 60 * 24 * 120; // ~120 days
    return list
      .filter((item) => item && item.slug && item.lastVisited >= cutoff)
      .sort((a, b) => b.lastVisited - a.lastVisited)
      .slice(0, 10);
  } catch {
    return [];
  }
}

export default function WelcomeView() {
  const [recentCalendars, setRecentCalendars] = useState<RecentCalendar[]>([]);
  const [unreadCounts, setUnreadCounts] = useState<Record<string, number>>({});

  useEffect(() => {
    setRecentCalendars(getRecentCalendars());
  }, []);

  // Initial unread counts from API
  useEffect(() => {
    const slugs = recentCalendars.map((c) => c.slug).filter(Boolean);
    if (slugs.length === 0) {
      setUnreadCounts({});
      return;
    }
    const params = new URLSearchParams({ slugs: slugs.join(",") });
    fetch(`/api/unread-counts?${params.toString()}`, { credentials: "same-origin" })
      .then((res) => (res.ok ? res.json() : {}))
      .then((data: Record<string, number>) => setUnreadCounts(data || {}))
      .catch(() => setUnreadCounts({}));
  }, [recentCalendars]);

  // WebSocket per recent calendar: when an event is received, increment that calendar's unread count in RT
  useEffect(() => {
    const slugs = recentCalendars.map((c) => c.slug).filter(Boolean);
    if (slugs.length === 0) return;

    const sockets: WebSocket[] = [];
    const proto = typeof window !== "undefined" && window.location?.protocol === "https:" ? "wss:" : "ws:";
    const host = typeof window !== "undefined" ? window.location.host : "";

    slugs.forEach((slug) => {
      const url = `${proto}//${host}/c/${slug}/ws`;
      const ws = new WebSocket(url);
      sockets.push(ws);
      ws.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data as string);
          if (data?.kind === "event" && data?.event) {
            setUnreadCounts((prev) => ({
              ...prev,
              [slug]: (prev[slug] ?? 0) + 1,
            }));
          }
        } catch {
          // ignore
        }
      };
    });

    return () => {
      sockets.forEach((s) => {
        if (s.readyState === WebSocket.OPEN || s.readyState === WebSocket.CONNECTING) {
          s.close();
        }
      });
    };
  }, [recentCalendars]);

  return (
    <div className="min-h-screen flex flex-col">
      <HeaderMinimal />
      <main className="flex-1 flex flex-col items-center justify-around p-6 text-center w-full">
        <h2 className="font-logo text-8xl sm:text-9xl md:text-[10rem] lg:text-[12rem] font-bold mb-10 tracking-tight uppercase brutal-text-shadow w-full px-0">
          <span className="text-brutal-orange dark:text-brutal-orange-dark">Meet</span>
          <wbr />
          <span className="text-brutal-purple dark:text-brutal-purple-hover">day</span>
        </h2>
        <div className="w-full max-w-2xl mx-auto flex flex-col items-center text-center gap-6">
          <form method="post" action="/" className="m-0 w-full">
            <BrutalButton type="submit" variant="primary" className="px-6 py-3 text-base min-h-[2.75rem] w-full">
              Create new calendar
            </BrutalButton>
          </form>

          {recentCalendars.length > 0 ? (
            <div className="w-full flex flex-col gap-0 border-2 border-black dark:border-gray-100 bg-white dark:bg-gray-950">
              <div className="px-4 py-2 border-b-2 border-black dark:border-gray-100 bg-gray-100 dark:bg-gray-900">
                <h3 className="m-0 font-logo font-bold text-sm uppercase tracking-tight text-gray-900 dark:text-white">
                  Recent calendars
                </h3>
              </div>
              <ul className="m-0 p-0 list-none flex flex-col">
                {recentCalendars.map((cal) => {
                  const newCount = unreadCounts[cal.slug] ?? 0;
                  return (
                    <li key={cal.slug} className="border-b-2 border-black dark:border-gray-100 last:border-b-0">
                      <a
                        href={`/c/${cal.slug}`}
                        className="block px-4 py-3 font-logo font-semibold text-sm uppercase tracking-tight text-gray-900 dark:text-gray-100 hover:bg-brutal-orange hover:text-black dark:hover:text-gray-900 transition-colors flex items-center justify-between gap-2"
                      >
                        <span className="truncate">{(cal.name && cal.name.trim()) || cal.slug}</span>
                        {newCount > 0 && (
                          <span className="shrink-0 text-xs font-bold bg-brutal-orange dark:bg-brutal-orange-dark text-black dark:text-gray-900 px-2 py-0.5 rounded">
                            {newCount} new
                          </span>
                        )}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : null}

          <Container className="text-center">
            <p className="m-0">Creates a calendar. You get a link. Share the link. Others open it and add when they are free. You see when everyone can meet.</p>
          </Container>
        </div>
      </main>
      <footer className="flex-shrink-0 border-t-4 border-black dark:border-gray-100 bg-gray-200 dark:bg-gray-800 py-4 px-6 text-center">
        <p className="m-0 text-sm font-logo font-bold uppercase tracking-tight text-gray-900 dark:text-gray-100">
          © 2026 Coralbits SL
        </p>
      </footer>
    </div>
  );
}
